import React, { useState, useEffect } from 'react';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Badge } from '@vaiu/ui/badge';
import { useNFC } from '@/hooks/useNFC';
import { useToast } from '@/hooks/use-toast';
import { 
  Smartphone, 
  Wifi, 
  WifiOff, 
  CreditCard, 
  AlertCircle, 
  CheckCircle,
  Loader2,
  Zap
} from 'lucide-react';

interface NFCPaymentProps {
  tokenId: string;
  cardNumber: string;
  expiryDate: string;
  cardholderName: string;
  onPaymentSent?: () => void;
}

const NFCPayment: React.FC<NFCPaymentProps> = ({
  tokenId,
  cardNumber,
  expiryDate,
  cardholderName,
  onPaymentSent
}) => {
  const { 
    isSupported, 
    isEnabled, 
    isWriting, 
    error, 
    writePaymentData, 
    requestPermission 
  } = useNFC();
  const { toast } = useToast();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(isSupported && isEnabled);
  }, [isSupported, isEnabled]);

  const handleNFCPayment = async () => {
    if (!isReady) {
      // Try to request permission first
      const granted = await requestPermission();
      if (!granted) {
        toast({
          title: "NFC Permission Required",
          description: "Please enable NFC in your device settings and grant permission to use NFC.",
          variant: "destructive"
        });
        return;
      }
    }

    try {
      await writePaymentData({
        cardNumber,
        expiryDate,
        cvv: '', // Don't transmit CVV for security
        cardholderName,
        tokenId
      });

      toast({
        title: "Payment Data Ready",
        description: "Hold your phone near the payment terminal to complete the transaction.",
        variant: "default"
      });

      onPaymentSent?.();
    } catch (err: any) {
      toast({
        title: "NFC Payment Failed",
        description: err.message || "Failed to prepare payment data for NFC transmission.",
        variant: "destructive"
      });
    }
  };

  const renderNFCStatus = () => {
    if (!isSupported) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            NFC is not supported on this device or browser.
          </AlertDescription>
        </Alert>
      );
    }

    if (!isEnabled) {
      return (
        <Alert variant="destructive">
          <WifiOff className="h-4 w-4" />
          <AlertDescription>
            NFC is disabled. Please enable NFC in your device settings.
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Alert>
        <CheckCircle className="h-4 w-4" />
        <AlertDescription>
          NFC is ready for payment transmission.
        </AlertDescription>
      </Alert>
    );
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Smartphone className="w-5 h-5" />
          <span>NFC Payment</span>
          <Badge variant={isReady ? "default" : "secondary"}>
            {isReady ? "Ready" : "Not Ready"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* NFC Status */}
        {renderNFCStatus()}

        {/* Card Information */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <CreditCard className="w-6 h-6" />
            <span className="text-sm font-medium">Prepaid Visa</span>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-mono">
              **** **** **** {cardNumber.slice(-4)}
            </p>
            <p className="text-xs">{cardholderName}</p>
            <p className="text-xs">Expires: {expiryDate}</p>
          </div>
        </div>

        {/* Payment Instructions */}
        <div className="text-sm text-gray-600 space-y-2">
          <p className="font-medium">How to use NFC Payment:</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Tap "Prepare Payment" below</li>
            <li>Hold your phone near the payment terminal</li>
            <li>Wait for the transaction to complete</li>
          </ol>
        </div>

        {/* Action Button */}
        <Button 
          onClick={handleNFCPayment}
          disabled={!isSupported || isWriting}
          className="w-full"
          size="lg"
        >
          {isWriting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Preparing Payment...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              Prepare NFC Payment
            </>
          )}
        </Button>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Warning */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>Note:</strong> NFC payment support is experimental and may not work with all PoS terminals. 
            Web NFC is currently only supported in Chrome on Android.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default NFCPayment; 